import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Shield, Zap } from 'lucide-react';
import ElectricalServices from '@mui/icons-material/ElectricalServices';
import SolarPower from '@mui/icons-material/SolarPower';
import AllInclusive from '@mui/icons-material/AllInclusive';
import TrendingDown from '@mui/icons-material/TrendingDown';

export default function VelkommenInfo() {
  return (
    <section id="velkommen-info" className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* What happened */}
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a2e1a] mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              Hvad sker der ved et tvunget leverandørskift?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Efter elforsyningslovens § 72 e kan Energinet via DataHub fordele aftagenumre til nye elhandelsvirksomheder,
              når en tidligere elhandelsvirksomhed ikke længere kan levere strøm. Hvis du er berørt, kan du være blevet
              tildelt enkel energi som din nye elhandelsvirksomhed, så din strøm fortsætter uden afbrydelse.
            </p>
          </div>

          {/* Key facts grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-[#f5f3ee] rounded-2xl p-6 border border-[#e8e4d9]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1a2e1a] rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#eab308]" />
                </div>
                <h3 className="font-bold text-[#1a2e1a]">Ingen afbrydelse</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Ved et tvunget leverandørskift er strømmen på intet tidspunkt i fare for at blive afbrudt. Den nye leverandør overtager ansvaret fra den dag, Energinet gennemfører fordelingen via DataHub.
              </p>
            </div>

            <div className="bg-[#f5f3ee] rounded-2xl p-6 border border-[#e8e4d9]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1a2e1a] rounded-xl flex items-center justify-center">
                  <ElectricalServices sx={{ fontSize: 20, color: '#eab308' }} />
                </div>
                <h3 className="font-bold text-[#1a2e1a]">Dit lovbestemte produkt</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Ved et tvunget leverandørskift er vi <strong>ifølge loven</strong> forpligtet til at placere dig på en reguleret
                <strong> overgangsaftale</strong>: <strong>0 kr./md. i abonnement</strong> + spotpris + et lovbestemt tillæg fastsat af
                <a href="https://afg.forsyningstilsynet.dk/" target="_blank" rel="noopener noreferrer" className="text-[#1a2e1a] font-semibold underline">Forsyningstilsynet</a> (§ 72 f). Se det præcise tillæg og prisberegningen længere oppe.
                Du kan til enhver tid skifte til en af vores normale aftaler – fra kun 18 kr./md.
              </p>
            </div>

            <div className="bg-[#f5f3ee] rounded-2xl p-6 border border-[#e8e4d9]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1a2e1a] rounded-xl flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#eab308]" />
                </div>
                <h3 className="font-bold text-[#1a2e1a]">Ingen binding</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Aftalen er bindende for enkel energi i 3 måneder fra overtagelsen. Du er ikke bundet til at blive hos os og
                <strong> kan opsige med 1 måneds varsel</strong> til udgangen af en måned – eller frit vælge en anden elhandelsvirksomhed.
              </p>
            </div>

            <div className="bg-[#f5f3ee] rounded-2xl p-6 border border-[#e8e4d9]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1a2e1a] rounded-xl flex items-center justify-center">
                  <SolarPower sx={{ fontSize: 20, color: '#eab308' }} />
                </div>
                <h3 className="font-bold text-[#1a2e1a]">Har du solceller?</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Hvis du har solcelleproduktion, følger den typisk med over til den nye leverandør. Hos enkel energi indgår det i <strong>Danmarks eneste gratis solcelleaftale</strong> – 
                0 kr. i leverandørgebyr, 0 kr. i månedlige tillæg. <Link to="/solcelleaftale" className="text-[#1a2e1a] font-semibold underline hover:text-[#0f1f12]">Læs mere →</Link>
              </p>
            </div>
          </div>

          {/* Hvem er enkel - Reassurance block */}
          <div className="bg-white border-2 border-[#1a2e1a] rounded-2xl p-6 md:p-8 mb-12">
            <h3 className="text-xl font-bold text-[#1a2e1a] mb-4 text-center" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Hvem er enkel energi?</h3>
            <p className="text-gray-600 text-center mb-6 max-w-2xl mx-auto">
              Vi er et elselskab, der – som navnet siger – gør el mere enkelt. Skifter du fra overgangsaftalen til en af vores
              normale aftaler, får du <strong>spotpris uden tillæg</strong> fra 18 kr./md.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: <TrendingDown sx={{ fontSize: 20, color: '#eab308' }} />, title: 'Spotpris 1:1', desc: 'Uden tillæg – du betaler den rene markedspris' },
                { icon: <AllInclusive sx={{ fontSize: 20, color: '#eab308' }} />, title: 'Alt inkluderet', desc: 'Saving Mode, serviceaftale, betalingsservice – 0 kr./md.' },
                { icon: <SolarPower sx={{ fontSize: 20, color: '#eab308' }} />, title: 'Gratis solcelleaftale', desc: 'Danmarks eneste – 0 kr. i leverandørgebyr' },
                { icon: <Zap className="w-5 h-5 text-[#eab308]" />, title: 'Laderabat for elbil', desc: '10 øre af spotprisen pr. kWh ved hjemmeopladning via Saving Mode (kompatibel ladeboks og bil)' }
              ].map((item, idx) => (
                <div key={idx} className="text-center p-4 bg-[#f5f3ee] rounded-xl">
                  <div className="w-10 h-10 bg-[#1a2e1a] rounded-lg flex items-center justify-center mx-auto mb-3">{item.icon}</div>
                  <h4 className="font-semibold text-[#1a2e1a] text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <a href="https://stromligning.dk/?forbrugsprofil=10000" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
                <span className="text-xs font-bold text-[#1a2e1a]">5/5</span>
                <img src="https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/da42c3e86_953b544b6_image.png" alt="Stromligning.dk" className="h-5" />
              </a>
              <a href="https://www.elbiil.dk/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
                <span className="text-xs font-bold text-[#1a2e1a]">5/5</span>
                <img src="https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/f14b91d3d_ede956df9_image.png" alt="elbiil.dk" className="h-5" />
              </a>
            </div>
          </div>

          {/* A conto warning */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-600 text-lg">⚠️</span>
              </div>
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Vedrørende aconto-indestående hos din tidligere leverandør</h3>
                <p className="text-amber-800 text-sm leading-relaxed">
                  Har du betalt for meget aconto hos din tidligere leverandør, har du et krav på tilbagebetaling i forbindelse
                  med slutafregningen. Beløbet <strong>overføres ikke til enkel energi</strong> og udbetales ikke af os.
                </p>
              </div>
            </div>
          </div>

          {/* Business + Betalingsservice note */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <span className="text-blue-600 text-lg mt-0.5">💡</span>
                <div>
                  <h4 className="font-bold text-blue-900 mb-1 text-sm">Betalingsservice</h4>
                  <p className="text-blue-800 text-xs leading-relaxed">
                    Når du modtager din første regning fra enkel energi, kan du tilmelde Betalingsservice, hvis du ønsker det. Betalingsservice overføres ikke automatisk fra din tidligere leverandør. Inkluderet til <strong>0 kr./md.</strong>
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <span className="text-gray-600 text-lg mt-0.5">🏢</span>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 text-sm">Erhvervskunder</h4>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Er du erhvervskunde og berørt af et leverandørskift? Vi hjælper gerne med at finde den bedste løsning.
                    <Link to="/supportkundeportal" className="text-[#1a2e1a] font-semibold underline ml-1">Kontakt os →</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}